// CoachLayer — the single portal-style renderer for the unified Coach/Toast FSM
// (plan: coach-tooltip-system.md §4.2). Mounted ONCE per shell (mobile + desktop),
// firewall-safe: the store lives in state/coachStore, this renderer in shared-ui/,
// each shell imports it independently (mirrors the <ImageLightbox/> precedent).
//
// PURE RENDERER + view-lifecycle: it reads `active`/`phase` via store selectors and
// owns only the VIEW transitions — the × button calls dismiss(), the ttl timer calls
// autoDismiss(), and the exit-animation end calls settle() (instant under
// prefers-reduced-motion). All DOMAIN triggers (offer/evaluate/arm/markDiscovered)
// live in the shells' useChatShell. State transitions go through named store actions,
// never CustomEvents (R-A2).
//
// TWO render paths, one mechanism:
//  • anchor:'viewport'      — a position:fixed bottom-center band with a max-width clamp
//                             and wrapping text (the Issue-2 fix folded in).
//  • anchor:{selector}      — a positioned, collision-flipping popover beside the
//                             data-coach-id target (Phase 3a). Placement geometry lives
//                             in the pure utils/popoverPlacement; the renderer only feeds
//                             it live rects and applies the result. Anchor-absent →
//                             nothing visible (the store's mountedAnchors gate already
//                             keeps such rows un-promotable — SE-09).

import { useEffect, useRef, useState } from 'preact/hooks';
import {
  useActiveCoach,
  useCoachPhase,
  dismiss as coachDismiss,
  autoDismiss as coachAutoDismiss,
  settle as coachSettle,
  promoteImmediate as coachPromoteImmediate,
  type CoachInstance,
} from '../state/coachStore';
import { computePopoverPlacement, type Placement } from '../utils/popoverPlacement';

function prefersReducedMotion(): boolean {
  return typeof matchMedia !== 'undefined' && matchMedia('(prefers-reduced-motion: reduce)').matches;
}

// The x dismiss always calls the store's dismiss(id); if the instance carries an
// onDismiss side effect (e.g. pwa-install's 30-day-cooldown write), run it first.
function handleClose(active: CoachInstance): void {
  active.onDismiss?.();
  coachDismiss(active.id);
}

// Optional secondary CTA (e.g. "Install") rendered before the x dismiss — generic,
// kind-agnostic (any toast/coach MAY carry one via ShowRequest.action).
function CoachAction({ active }: { active: CoachInstance }) {
  if (!active.action) return null;
  const { label, onClick } = active.action;
  return (
    <button class="coach-toast-action" onClick={onClick}>
      {label}
    </button>
  );
}

// Element-anchored popover: resolve the data-coach-id target, position beside it via the
// pure placement util, and re-measure on scroll/resize (passive listeners — the anchor
// only moves on those, so no per-frame rAF). Rendered hidden until measured to avoid a
// flash at (0,0); if the anchor isn't in the DOM it stays hidden (store gate makes this rare).
function CoachPopover({
  active,
  dismissing,
  isError,
}: {
  active: CoachInstance;
  dismissing: boolean;
  isError: boolean;
}) {
  const selector = active.anchor !== 'viewport' ? active.anchor.selector : '';
  const popRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<Placement | null>(null);

  useEffect(() => {
    if (!selector) return;
    const measure = () => {
      const el = document.querySelector(selector);
      const pop = popRef.current;
      if (!el || !pop) {
        setPos(null);
        return;
      }
      const a = el.getBoundingClientRect();
      const size = { width: pop.offsetWidth, height: pop.offsetHeight };
      const vp = { width: window.innerWidth, height: window.innerHeight };
      setPos(computePopoverPlacement(a, size, vp));
    };
    measure();
    window.addEventListener('resize', measure);
    // capture → catches scroll in ANY container; passive → never blocks scrolling.
    window.addEventListener('scroll', measure, { capture: true, passive: true });
    return () => {
      window.removeEventListener('resize', measure);
      window.removeEventListener('scroll', measure, { capture: true });
    };
  }, [selector]);

  return (
    <div
      ref={popRef}
      class={`coach-popover${pos ? ` coach-popover--${pos.side}` : ''}${dismissing ? ' coach-popover--out' : ''}${active.persona ? ' coach-popover--persona' : ''}`}
      style={{
        position: 'fixed',
        top: `${pos?.top ?? 0}px`,
        left: `${pos?.left ?? 0}px`,
        maxWidth: 'min(92vw, 320px)',
        visibility: pos ? 'visible' : 'hidden',
      }}
      role={isError ? 'alert' : 'status'}
      aria-live={isError ? 'assertive' : 'polite'}
    >
      <span class="coach-popover-body">{active.body}</span>
      <CoachAction active={active} />
      {active.dismissible && (
        <button
          class="coach-toast-close"
          onClick={() => handleClose(active)}
          aria-label="Dismiss"
        >
          ×
        </button>
      )}
    </div>
  );
}

export function CoachLayer() {
  const active = useActiveCoach();
  const phase = useCoachPhase();
  const activeId = active?.id ?? null;
  const activeTtl = active?.ttlMs;

  // ttl → autoDismiss (showing only). Re-armed per active instance.
  useEffect(() => {
    if (!activeId || phase !== 'showing' || !activeTtl) return;
    const t = setTimeout(() => coachAutoDismiss(activeId), activeTtl);
    return () => clearTimeout(t);
  }, [activeId, phase, activeTtl]);

  // dismissing → settle (frees the slot). Instant under reduced-motion; otherwise
  // after the exit animation (kept in lockstep with the CSS .coach-toast--out
  // transition duration). settle() is the named `settled` action — never a bare timer.
  // promoteImmediate() right after gives a queued toast/pill/forced-coach the freed
  // slot without waiting for the next domain evaluate() tick (Phase 3b).
  useEffect(() => {
    if (phase !== 'dismissing') return;
    const delay = prefersReducedMotion() ? 0 : 200;
    const t = setTimeout(() => {
      coachSettle();
      coachPromoteImmediate();
    }, delay);
    return () => clearTimeout(t);
  }, [phase]);

  if (!active) return null;

  const dismissing = phase === 'dismissing';
  const isError = active.role === 'alert';

  // Element-anchored rows → the collision-flipping popover (Phase 3a).
  if (active.anchor !== 'viewport') {
    return <CoachPopover active={active} dismissing={dismissing} isError={isError} />;
  }

  // Viewport rows → the bottom-center band (the migrated swipe hint + global hints).
  return (
    <div
      class={`coach-toast${dismissing ? ' coach-toast--out' : ''}${active.persona ? ' coach-toast--persona' : ''}`}
      style={{ position: 'fixed', maxWidth: 'min(92vw, 420px)' }}
      role={isError ? 'alert' : 'status'}
      aria-live={isError ? 'assertive' : 'polite'}
    >
      <span class="coach-toast-body">{active.body}</span>
      <CoachAction active={active} />
      {active.dismissible && (
        <button
          class="coach-toast-close"
          onClick={() => handleClose(active)}
          aria-label="Dismiss"
        >
          ×
        </button>
      )}
    </div>
  );
}
